import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface AdminCompanyUser {
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string;
  onboarding_complete: boolean;
  created_at: string;
}

export interface AdminCompanyUsersResult {
  users: AdminCompanyUser[];
}

/**
 * Hook to fetch company users for admin dashboard
 * Uses separate queries to avoid RLS join issues
 */
export function useAdminCompanyUsers(companyId: string | null) {
  return useQuery<AdminCompanyUsersResult>({
    queryKey: ['admin', 'company-users', companyId],
    enabled: !!companyId,
    staleTime: 30_000, // 30 seconds
    queryFn: async () => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }

      // Prefer RPC to avoid RLS issues for admin/developer
      const { data: companyUsersData, error: companyUsersError } = await (supabase.rpc as any)(
        'get_company_users',
        { p_company_id: companyId },
      );

      if (companyUsersError) {
        console.error('[useAdminCompanyUsers] Error loading company users:', companyUsersError);
        throw companyUsersError;
      }

      const rawUsers = (companyUsersData || []) as Array<{
        user_id: string;
        email: string | null;
        first_name: string | null;
        last_name: string | null;
        role: string | null;
        created_at: string | null;
      }>;

      if (rawUsers.length === 0) {
        return { users: [] };
      }

      // Best-effort onboarding status (don't block if RLS denies)
      const userIds = rawUsers.map((user) => user.user_id);
      let onboardingMap = new Map<string, boolean>();
      const { data: onboardingData, error: onboardingError } = await supabase
        .from('user_company_relationships')
        .select('user_id, onboarding_complete')
        .eq('company_id', companyId)
        .in('user_id', userIds);

      if (!onboardingError && onboardingData) {
        onboardingMap = new Map(onboardingData.map((row) => [row.user_id, row.onboarding_complete]));
      }

      const users: AdminCompanyUser[] = rawUsers.map((user) => ({
        user_id: user.user_id,
        email: user.email || null,
        first_name: user.first_name || null,
        last_name: user.last_name || null,
        role: user.role || 'unknown',
        onboarding_complete: onboardingMap.get(user.user_id) ?? false,
        created_at: user.created_at || new Date().toISOString(),
      }));

      return { users };
    },
  });
}
